/**
 * Helper functions for URL-synced localStorage operations.
 * Used by createURLLocalStorage to handle state-to-URL conversions.
 */

/**
 * Parse Zustand's persisted format: { state: {...}, version: 0 }
 * Falls back to the value itself if not wrapped
 */
export function parsePersistedZustandState(value: string): any {
  const persisted = JSON.parse(value);
  return persisted.state || persisted;
}

/**
 * Filter out invalid values from arrays.
 * Removes null, undefined, and the string 'undefined' (which can appear
 * due to Zustand's serialization of undefined values)
 */
export function filterValidArrayValues(arr: any[]): any[] {
  return arr.filter(v => v && v !== 'undefined');
}

/**
 * Convert a single value to URL parameter string format.
 * Returns null for empty/invalid values to indicate param should be deleted.
 */
export function convertValueToURLParam(val: any): string | null {
  if (val === null || val === undefined) {
    return null;
  }

  if (Array.isArray(val)) {
    const filtered = filterValidArrayValues(val);
    return filtered.length > 0 ? filtered.join(',') : null;
  }

  return String(val);
}

/**
 * Convert state object to URL parameter entries.
 * Handles nested objects by flattening them into top-level params.
 * For example: { filters: { status: 'active' } } becomes { status: 'active' }
 */
export function convertStateToURLEntries(state: any): Record<string, string | null> {
  const urlEntries: Record<string, string | null> = {};

  Object.entries(state).forEach(([key, val]) => {
    if (typeof val === 'object' && !Array.isArray(val) && val !== null) {
      // Nested object: flatten each property into separate URL params
      Object.entries(val).forEach(([nestedKey, nestedVal]) => {
        if (Array.isArray(nestedVal)) {
          const filtered = filterValidArrayValues(nestedVal);
          urlEntries[nestedKey] = filtered.length > 0 ? filtered.join(',') : null;
        } else if (nestedVal) {
          urlEntries[nestedKey] = String(nestedVal);
        }
      });
    } else {
      // Primitive or array: convert directly
      urlEntries[key] = convertValueToURLParam(val);
    }
  });

  return urlEntries;
}

/**
 * Collect all keys from state, including nested object keys.
 * Used when removing state to know which URL params to clear.
 */
export function collectAllStateKeys(state: any): string[] {
  const keys: string[] = [];

  Object.entries(state).forEach(([key, val]) => {
    if (typeof val === 'object' && !Array.isArray(val) && val !== null) {
      // Nested object: collect all nested keys
      keys.push(...Object.keys(val));
    } else {
      keys.push(key);
    }
  });

  return keys;
}
